import type { ICustomControlProcedureDiagnosticReportAddEditProps } from '../../interfaces/';
import { ProcessEntity } from '../../../../../../common/enums/processEntity';
import { ToastType } from '../../../../../../common/enums/toastType';
import { translateKey } from '../../../../../translate';
import { renameErrorKeys } from '../../../../../utils/renameErrorKeys';
import { showServiceToast } from '../../../../../utils/showToast';
import { CancelButton } from '../../../../elements/carePathButtons/CancelButton';
import { SaveButton } from '../../../../elements/carePathButtons/SaveButton';
import { SaveSendButton } from '../../../../elements/carePathButtons/SaveSendButton';
import { ButtonContainer, Form } from '../../../../elements/common';
import { Component } from '../../../../elements/Component';
import { ProceedWithoutFiles } from '../../../../elements/ProceedWithoutFiles';
import { NewOrEditDiagnosticReportProcessContainer } from '../../utils/v1/NewOrEditDiagnosticReportProcessContainer';
import { TpoV1DiagnosticReportAddEditCustomControlFields } from './TpoV1DiagnosticReportAddEditCustomControlFields';
import { TpoV1DiagnosticReportFollowingSteps } from './TpoV1DiagnosticReportFollowingSteps';
import { TpoV1ProcedureAddEdit } from './TpoV1ProcedureAddEdit';

interface IInnerTpoV1ProcedureDiagnosticReportAddEditState {
    buttonDisabled?: boolean;
    isProcessing?: boolean;
    proceedWithoutFiles?: boolean;
    showProceedWithoutFiles?: boolean;
}

export function TpoV1ProcedureDiagnosticReportAddEdit(props: ICustomControlProcedureDiagnosticReportAddEditProps) {
    return <InnerTpoV1ProcedureDiagnosticReportAddEdit {...props} />;
}

class InnerTpoV1ProcedureDiagnosticReportAddEdit extends Component<
    ICustomControlProcedureDiagnosticReportAddEditProps,
    IInnerTpoV1ProcedureDiagnosticReportAddEditState
> {
    public render() {
        return (
            <>
                <TpoV1ProcedureAddEdit
                    {...this.props}
                    isBioGrader
                    setProcessing={async (isProcessing) => await this.setState({ isProcessing })}
                />

                <NewOrEditDiagnosticReportProcessContainer
                    currentDiagnosticReport={this.props.currentDiagnosticReport}
                    service={this.props.service}
                >
                    <Form
                        onSubmit={() => null}
                        showOverlay={this.state.isProcessing}
                    >
                        <TpoV1DiagnosticReportAddEditCustomControlFields
                            customControlData={this.props.diagnosticReport.customControlData}
                            errors={renameErrorKeys(
                                this.props.errors,
                                'diagnosticReport.customControlData',
                                'customControlData'
                            )}
                            handleChangeCustomControl={this.props.handleChangeCustomControlDiagnosticReport}
                            validateOneCustomControl={this.props.validateOneCustomControlDiagnosticReport}
                        />

                        <TpoV1DiagnosticReportFollowingSteps />

                        <ButtonContainer
                            alignRight
                            buttons={[
                                <CancelButton
                                    cancel={this.props.onCancel}
                                    entity={ProcessEntity.procedure}
                                    isExisting={!!this.props.request.id}
                                    serviceCode={this.props.service.code}
                                    serviceType={this.props.service.serviceType}
                                />,
                                <SaveButton
                                    buttonDisabled={this.state.buttonDisabled}
                                    save={async () => {
                                        await this.saveProcedure('draft');
                                    }}
                                />,
                                <SaveSendButton
                                    buttonDisabled={this.state.buttonDisabled}
                                    saveSend={async () => {
                                        await this.saveProcedure('final');
                                    }}
                                />,
                            ]}
                        />
                    </Form>
                </NewOrEditDiagnosticReportProcessContainer>

                {this.state.showProceedWithoutFiles && (
                    <ProceedWithoutFiles
                        entity={ProcessEntity.procedure}
                        onNo={async () =>
                            await this.setState({ showProceedWithoutFiles: false, buttonDisabled: false })
                        }
                        onYes={async () => {
                            await this.setState({ showProceedWithoutFiles: false, proceedWithoutFiles: true });
                            await this.saveProcedure('final');
                        }}
                        serviceType={this.props.service.serviceType}
                    />
                )}
            </>
        );
    }

    private async saveProcedure(saveType: 'draft' | 'final') {
        await this.setState({ buttonDisabled: true });

        const validatedProcedure = await this.props.validateProcedure(saveType);
        if (!validatedProcedure) {
            await this.setState({ buttonDisabled: false });

            return;
        }

        if (
            !this.state.proceedWithoutFiles &&
            saveType === 'final' &&
            this.props.procedure.assets.length === 0 &&
            (!this.props.procedure.customControlData.files || this.props.procedure.customControlData.files.length === 0)
        ) {
            return await this.setState({ showProceedWithoutFiles: true });
        }

        const requestId = await this.props.onSaveProcedure(saveType);
        await this.setState({ buttonDisabled: false });
        if (!requestId) {
            return;
        }

        if (saveType === 'final') {
            showServiceToast(
                ToastType.success,
                'toast.submitSuccess',
                this.props.service,
                'procedure',
                translateKey('toast.submitDefaultMessage')
            );
        } else {
            showServiceToast(
                ToastType.success,
                'toast.draftSuccess',
                this.props.service,
                'procedure',
                translateKey('toast.draftDefaultMessage')
            );
        }

        await this.props.onSave(requestId);
    }
}
